import React from "react";
import { Monitor, Laptop, FileText } from "lucide-react";
import {
  SCROLL_OFFSET,
  ARTICLE_COLUMN,
  H2,
  BODY,
  BODY_SM,
  STACK_GAP,
} from "./sections";

const TOOLS = [
  {
    icon: Monitor,
    lead: "Windows Voice Access",
    body: " (or the older Windows Speech Recognition): the system-level dictation built into Windows, triggered with Win + H.",
  },
  {
    icon: Laptop,
    lead: "macOS Dictation",
    body: ": Apple's built-in speech-to-text, available system-wide via a keyboard shortcut or the Fn key.",
  },
  {
    icon: FileText,
    lead: "Google Docs Voice Typing",
    body: ": found under Tools > Voice typing, and limited to the Docs environment itself.",
  },
];

const WhatCountsSection = () => {
  return (
    <section
      id="what-counts-as-built-in-dictation"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      {/* Article intro - sits above the first heading, level with the rail */}
      <p className={BODY}>
        In practice, most people who try built-in dictation for real work
        (emails, reports, client notes, long-form writing) give up on it within
        a week. The gap between &quot;it can technically transcribe
        speech&quot; and &quot;it&apos;s actually usable for daily work&quot;
        is where AI voice typing tools like Zero Typing come in.
      </p>

      <p className={`${STACK_GAP} ${BODY}`}>
        This post breaks down how the built-in dictation tools on Windows, Mac,
        and Google Docs actually compare to a dedicated AI voice typing app, so
        you can see exactly where each one holds up and where it doesn&apos;t.
      </p>

      <h2 className={`mt-[40px] md:mt-[48px] ${H2}`}>
        What Counts as &quot;Built-In Dictation&quot;?
      </h2>

      <p className={`${STACK_GAP} ${BODY}`}>
        Before comparing anything, it helps to separate the three tools people
        usually mean when they say &quot;built-in dictation&quot;:
      </p>

      <div className="mt-[30.05px] flex flex-col gap-[17.17px]">
        {TOOLS.map(({ icon: Icon, lead, body }) => (
          <article
            key={lead}
            className="flex items-start gap-[12.88px] rounded-[17.17px] border-[1px] border-[#3AB1F633] bg-[#EBF7FE] p-[16px] sm:p-[21.46px]"
          >
            {/* Filled icon tile, same footprint as the tick box on the other guides */}
            <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-[#3AB1F6] text-white">
              <Icon className="h-[11px] w-[11px]" strokeWidth={2.4} />
            </span>

            <p className={BODY_SM}>
              <strong className="font-bold text-[#0A0D16]">{lead}</strong>
              {body}
            </p>
          </article>
        ))}
      </div>

      <p className={`mt-[30.05px] ${BODY}`}>
        All three share the same basic limitation: they were built to
        transcribe, not to help you write. That distinction matters more than
        it sounds.
      </p>
    </section>
  );
};

export default WhatCountsSection;
